import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@lomarengas/design-system';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getBlockedPeriodDetails,
  initialBlockedPeriod,
  saveOrUpdateBlockedPeriod,
  selectBlockedPeriodDetails,
  selectBlockedPeriodDetailsStatus,
} from './blockedPeriodDetailsSlice';
import { addStatusToast, SectionHeader } from '../../common/components';
import { LOADING_STATUS } from '../../common/constants';
import { BlockedPeriodForm } from './components/BlockedPeriodForm';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';

const BlockedPeriodDetails = () => {
  const { blockedPeriodId } = useParams<{ blockedPeriodId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accommodationId = queryParams.get('accommodationId');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const details = useAppSelector(selectBlockedPeriodDetails);
  const status = useAppSelector(selectBlockedPeriodDetailsStatus);
  const inputErrors = useAppSelector(selectInputErrors);

  const [localDetails, setLocalDetails] = useState(details);

  const backAccommodationId = accommodationId || localDetails?.accommodationId;
  const backLinkUrl = backAccommodationId
    ? `/rates/blockedperiods/${backAccommodationId}`
    : '/rates/blockedperiods';

  useEffect(() => {
    if (blockedPeriodId === 'new') {
      // initialize new BlockedPeriodDetails with accommodationId from url params
      setLocalDetails({ ...initialBlockedPeriod, accommodationId });
    } else {
      dispatch(getBlockedPeriodDetails(blockedPeriodId));
    }
  }, [dispatch, blockedPeriodId, accommodationId]);

  useEffect(() => {
    if (blockedPeriodId === 'new') {
      setLocalDetails({ ...initialBlockedPeriod, accommodationId });
    } else {
      setLocalDetails(details);
    }
  }, [details, blockedPeriodId, accommodationId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalDetails(prevDetails => ({ ...prevDetails, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setLocalDetails(prevDetails => ({ ...prevDetails, [name]: checked }));
  };

  const handleDateChange = (field: string, value: string | Date) => {
    setLocalDetails(prevDetails => ({
      ...prevDetails,
      [field]: value instanceof Date ? format(value, 'yyyy-MM-dd') : value,
    }));
  };

  const handleSelectChange = (newValue: { value: string; label: string }) => {
    setLocalDetails(prevDetails => ({
      ...prevDetails,
      type: newValue.value,
    }));
  };

  const handleSave = () => {
    dispatch(saveOrUpdateBlockedPeriod(localDetails))
      .unwrap()
      .then((savedBlockedPeriodId: string) => {
        // refetch blocked period details after saving / updating
        dispatch(getBlockedPeriodDetails(savedBlockedPeriodId));

        dispatch(
          addStatusToast({
            Message: `Estojakson tiedot päivitetty.`,
          }),
        );

        navigate(`/rates/blockedperiods/${localDetails.accommodationId}`);
      });
  };

  const isNewBlockedPeriod = blockedPeriodId === 'new';
  const isICalType = localDetails?.type === 'ICalImport';
  const isFormDisabled = !isNewBlockedPeriod && isICalType;
  const formatDate = (date?: string | Date) =>
    date
      ? new Date(date).toLocaleDateString('fi-FI', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '-';

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Estetty jakso</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Estetyt jaksot']}
          Title={
            blockedPeriodId === 'new'
              ? 'Uusi estetty jakso'
              : 'Estetty jakso - tietojen päivitys'
          }
        />

        <Loader IsLoading={status === LOADING_STATUS.LOADING} />

        {status === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            <Row>
              <Col>
                <InputErrorsDisplay inputErrors={inputErrors} />
              </Col>
            </Row>
            {isFormDisabled && (
              <Row>
                <Col>
                  <div className="alert alert-info">
                    iCal-tuodut estojaksot eivät ole muokattavissa. Ne
                    päivittyvät automaattisesti iCal-syötteestä.
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <BlockedPeriodForm
                  details={localDetails}
                  handleInputChange={handleInputChange}
                  handleDateChange={handleDateChange}
                  handleSelectChange={handleSelectChange}
                  handleCheckboxChange={handleCheckboxChange}
                  isNewBlockedPeriod={isNewBlockedPeriod}
                  isDisabled={isFormDisabled}
                />
                {isFormDisabled && (
                  <div className="mt-24">
                    <div className="mb-3">
                      <p>
                        Sisäänkirjautuminen: {localDetails.checkInTime || '-'}
                        <br />
                        Uloskirjautuminen: {localDetails.checkOutTime || '-'}
                      </p>
                    </div>
                    <div className="mb-3">
                      {localDetails?.iCalFullDescription && (
                        <p>{localDetails.iCalFullDescription}</p>
                      )}
                      {localDetails?.iCalLocation && (
                        <p>{localDetails.iCalLocation}</p>
                      )}
                    </div>
                    <div className="mb-3">
                      <strong>Ulkoinen tunniste (UID):</strong>
                      <p>{localDetails?.iCalExternalUid || 'Ei saatavilla'}</p>
                    </div>
                    {localDetails?.iCalDistributionChannelName && (
                      <div className="mb-3">
                        <strong>Kanava:</strong>
                        <p>{localDetails.iCalDistributionChannelName}</p>
                      </div>
                    )}
                    {(localDetails?.adults !== undefined ||
                      localDetails?.children !== undefined) && (
                      <div className="mb-3">
                        <strong>Henkilöt:</strong>
                        <p>
                          {localDetails?.adults ?? 0} +{' '}
                          {localDetails?.children ?? 0}
                        </p>
                      </div>
                    )}
                    {localDetails?.ownerBookingNotes && (
                      <div className="mb-3">
                        <strong>Lisätiedot:</strong>
                        <p>{localDetails.ownerBookingNotes}</p>
                      </div>
                    )}
                  </div>
                )}
                <Button
                  onClick={handleSave}
                  variant="primary"
                  className="w-100 mt-24"
                  disabled={isFormDisabled}
                >
                  Tallenna
                </Button>
              </Col>
            </Row>
          </Container>
        )}
      </Container>
    </>
  );
};

export default BlockedPeriodDetails;
